/** IXD vs Workbook 구조 비교 메인 페이지 */

import { useMemo } from "react";
import { ArrowLeft } from "lucide-react";

import { useIxdWorkbookCompareStore } from "@/stores/ixd-workbook-compare";
import { IxdWorkbookCompareStoreProvider } from "./compare-store-provider";
import { useIxdWorkbookCompare } from "@/hooks/ixd-workbook-compare/use-ixd-workbook-compare";
import { InitialUploadView } from "@/components/ixd-workbook-compare/initial-upload-view";
import {
  buildNoteEntries,
  IxdWorkbookCompareSidebar,
  noteHasChanges,
} from "@/components/ixd-workbook-compare/compare-sidebar";
import { NoteDiffDetail } from "@/components/ixd-workbook-compare/note-diff-detail";

/** 페이지 루트: Provider로 감싸서 페이지 스코프 스토어 제공 */
export const IxdWorkbookComparePage = () => (
  <IxdWorkbookCompareStoreProvider>
    <IxdWorkbookComparePageContent />
  </IxdWorkbookCompareStoreProvider>
);

const IxdWorkbookComparePageContent = () => {
  const ixdFile = useIxdWorkbookCompareStore((s) => s.ixdFile);
  const setIxdFile = useIxdWorkbookCompareStore((s) => s.setIxdFile);
  const selectedWorkbookId = useIxdWorkbookCompareStore(
    (s) => s.selectedWorkbookId,
  );
  const setSelectedWorkbookId = useIxdWorkbookCompareStore(
    (s) => s.setSelectedWorkbookId,
  );
  const result = useIxdWorkbookCompareStore((s) => s.result);
  const isCompareView = useIxdWorkbookCompareStore((s) => s.isCompareView);
  const activeNoteId = useIxdWorkbookCompareStore((s) => s.activeNoteId);
  const setActiveNoteId = useIxdWorkbookCompareStore((s) => s.setActiveNoteId);
  const backToUpload = useIxdWorkbookCompareStore((s) => s.backToUpload);

  const { isComparing, handleCompare, canCompare, error } =
    useIxdWorkbookCompare({ ixdFile, selectedWorkbookId });

  const notes = useMemo(
    () => (result ? buildNoteEntries(result) : []),
    [result],
  );

  const effectiveNoteId =
    activeNoteId ?? notes.find(noteHasChanges)?.id ?? notes[0]?.id ?? null;

  if (!isCompareView || !result) {
    return (
      <InitialUploadView
        ixdFile={ixdFile}
        onIxdSelect={setIxdFile}
        selectedWorkbookId={selectedWorkbookId}
        onWorkbookSelect={setSelectedWorkbookId}
        onCompare={handleCompare}
        canCompare={canCompare}
        isComparing={isComparing}
        errorMessage={error instanceof Error ? error.message : null}
      />
    );
  }

  const activeNote = notes.find((note) => note.id === effectiveNoteId) ?? null;

  const detail = activeNote ? (
    <NoteDiffDetail
      key={activeNote.id}
      note={activeNote}
      result={result}
      workbookId={selectedWorkbookId}
    />
  ) : (
    <div className="flex h-full items-center justify-center text-label-alternative">
      비교할 목차가 없습니다.
    </div>
  );

  return (
    <div className="flex h-screen flex-col bg-background-neutral-alternative">
      <header className="flex h-14 shrink-0 items-center gap-4 border-b border-neutral-200 bg-white px-6">
        <button
          type="button"
          aria-label="업로드 화면으로 돌아가기"
          onClick={backToUpload}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-semibold text-neutral-950">
          IXD / Workbook 구조 비교 결과
        </h1>
        <span className="text-sm text-label-alternative truncate">
          {ixdFile?.name}
        </span>
      </header>
      <div className="flex flex-1 min-h-0">
        <IxdWorkbookCompareSidebar
          notes={notes}
          activeNoteId={effectiveNoteId}
          onSelect={setActiveNoteId}
        />
        <main className="flex-1 min-w-0 overflow-y-auto">{detail}</main>
      </div>
    </div>
  );
};
